import React from "react";
import { type BuildAndBuyDispatchType, useBnbSelector } from "../../../../redux/store";
import { NavOrigin } from "../../../../../shared-logic/utils/Navigation";
import { BnbLabel } from "../../../../utils/labels";
import * as Button from "../../../../../../../common-deprecated/styles/v2/toyota/components/Button";
import { IconWrapper } from "../../../../../../../common-deprecated/styles/v2/toyota/globals/IconWrapper";
import Icon from "../../../../../../../common-deprecated/styles/v2/toyota/globals/Icon";
import * as FinanceStyles from "./styles/Finance";
import { MODAL_FINANCE_DISCLAIMER } from "../../../../../../../common-deprecated/utils/modalConstants";
import { showModal } from "../../../../../../../common-deprecated/redux/actions/ModalActions";
import MonthlyPrice from "../../common/price/MonthlyPrice";
import useFinanceToggle from "../../../../hooks/shared/actions/useFinanceToggle";
import InsurancePrice from "../../common/price/InsurancePrice";
import { hasInsuranceConfigured } from "../../../../utils/financeInsuranceUtils";
import SrOnly from "../../../../../../../common-deprecated/styles/v2/toyota/utilities/ScreenreaderOnly";
import { useFinanceInsurance, useFinanceInsuranceNavigation } from "../../../../hooks/shared/useFinanceInsurance";
import { useDispatch } from "../../../../../../../shared-logic/redux/store";

const FinanceInsuranceCta = (): JSX.Element | null => {
    const dispatch = useDispatch<BuildAndBuyDispatchType>();
    const bnbSettings = useBnbSelector((state) => state.bnbSettings);

    const { isPersonalizedQuote, info: insuranceInfo } = useBnbSelector((state) => state.insuranceRate);

    const step = useBnbSelector((state) => state.navigation.step);

    const { showMonthlyLoader } = useFinanceToggle();

    const { showFinance, showInsurance, monthlyFormat, totalPrice, financeInfo } = useFinanceInsurance();

    const { onClick: onCtaClick, financeLabelKey, insuranceLabelKey } = useFinanceInsuranceNavigation(step);

    return (
        <FinanceStyles.ButtonGroup>
            {showFinance && (
                <FinanceStyles.Button
                    as="a"
                    onClick={(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
                        onCtaClick(event, "cc-action", NavOrigin.FinancialCta);
                    }}
                >
                    <IconWrapper>
                        <Icon variant="card" size="medium" />
                    </IconWrapper>
                    <FinanceStyles.ButtonTextGroup>
                        <BnbLabel label={financeLabelKey} />
                        {totalPrice.monthly !== 0 && (
                            <FinanceStyles.ButtonFlexContent>
                                <MonthlyPrice
                                    price={totalPrice}
                                    priceFormat={monthlyFormat}
                                    showLoader={showMonthlyLoader}
                                    forceHideDisclaimer
                                    financeInfo={financeInfo}
                                    size="small"
                                />
                                {financeInfo && financeInfo.disclaimer && (
                                    <Button.Link
                                        type="button"
                                        hasIconRight
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            dispatch(
                                                showModal({
                                                    type: MODAL_FINANCE_DISCLAIMER,
                                                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                                    disclaimer: financeInfo.disclaimer!,
                                                    productName: "",
                                                }),
                                            );
                                        }}
                                    >
                                        <IconWrapper>
                                            <Icon variant="info" />
                                        </IconWrapper>
                                        <SrOnly>
                                            <BnbLabel label="bnbA11yButtonMoreInfo" />
                                        </SrOnly>
                                    </Button.Link>
                                )}
                            </FinanceStyles.ButtonFlexContent>
                        )}
                    </FinanceStyles.ButtonTextGroup>
                    <IconWrapper>
                        <Icon variant="arrowRight" />
                    </IconWrapper>
                </FinanceStyles.Button>
            )}
            {showInsurance && (
                <FinanceStyles.Button
                    as="a"
                    onClick={(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
                        onCtaClick(event, "cc-action", NavOrigin.InsuranceCta);
                    }}
                >
                    <IconWrapper>
                        <Icon variant="insurance" size="medium" />
                    </IconWrapper>
                    <FinanceStyles.ButtonTextGroup>
                        <BnbLabel label={insuranceLabelKey} />
                        {hasInsuranceConfigured(bnbSettings.requireDefaultInsurance, isPersonalizedQuote) && (
                            <FinanceStyles.ButtonFlexContent>
                                <InsurancePrice forceHideDisclaimer />
                                {insuranceInfo && insuranceInfo.disclaimer && (
                                    <Button.Link
                                        type="button"
                                        hasIconRight
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            dispatch(
                                                showModal({
                                                    type: MODAL_FINANCE_DISCLAIMER,
                                                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                                    disclaimer: insuranceInfo.disclaimer!,
                                                    productName: "",
                                                }),
                                            );
                                        }}
                                    >
                                        <IconWrapper>
                                            <Icon variant="info" />
                                        </IconWrapper>
                                        <SrOnly>
                                            <BnbLabel label="bnbA11yButtonMoreInfo" />
                                        </SrOnly>
                                    </Button.Link>
                                )}
                            </FinanceStyles.ButtonFlexContent>
                        )}
                    </FinanceStyles.ButtonTextGroup>
                    <IconWrapper>
                        <Icon variant="arrowRight" />
                    </IconWrapper>
                </FinanceStyles.Button>
            )}
        </FinanceStyles.ButtonGroup>
    );
};

export default FinanceInsuranceCta;
